/**
 * Transforms backend Olympics event data to frontend listable format
 *
 * Backend: results is always a Results object
 * Frontend gets clean discriminated union based on sport type
 */

import { OlympicsEvent } from '../validation/scheduleDataSchemas';
import {
  OlympicsListableEvent,
  IndividualSportEvent,
  TeamSportEvent,
  IndividualResultEntry,
  TeamResultEntry,
  SportsType,
  ProgressionStatus,
  MetricType,
  ResultMetric,
  ResultComment,
  NeverFinishedInfo,
  Standing,
} from '../types/olympicsListableEvent';

/**
 * Valid metric types to extract from results
 */
const VALID_METRIC_TYPES: MetricType[] = [
  'distance',
  'duration',
  'missed_shots',
  'points',
  'runningscore',
];

/**
 * Type guard to check if a string is a valid ProgressionStatus value
 */
function isProgressionStatus(value: string): value is ProgressionStatus {
  return (
    value === 'notstarted' ||
    value === 'inprogress' ||
    value === 'finished' ||
    value === 'cancelled' ||
    value === 'interrupted'
  );
}

/**
 * Type guard to check if a string is a valid ResultComment value
 */
function isResultComment(value: string): value is ResultComment {
  return value === 'DNS' || value === 'DNF' || value === 'DSQ' || value === 'Q';
}

/**
 * Type guard to check if a ResultComment is a NeverFinishedInfo value
 */
function isNeverFinishedInfo(value: ResultComment): value is NeverFinishedInfo {
  return value === 'DNS' || value === 'DNF' || value === 'DSQ';
}

/**
 * Check if a comment value indicates qualification (Q or Q/*)
 */
function isQualifiedCode(value: string): boolean {
  return value === 'Q' || value.startsWith('Q/');
}

/**
 * Extracts last name (surname) from a full name
 * Returns the last space-separated part of the name
 */
function extractSurname(fullName: string): string {
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts[parts.length - 1] : '';
}

/**
 * Normalizes country names from genitive to nominative form
 * Fixes systematic data errors where country names appear in wrong grammatical case
 */
function normalizeCountryName(name: string): string {
  // Fix "Ison-Britannian" (genitive) to "Iso-Britannia" (nominative)
  if (name === 'Ison-Britannian') {
    return 'Iso-Britannia';
  }
  return name;
}

/**
 * Extracts Finnish participant surnames from event participants
 *
 * @param eventParticipants - Array of event participants from backend
 * @returns Array of Finnish participant surnames (empty if no Finnish participants)
 */
export function extractFinnishParticipantSurnames(
  eventParticipants: Array<{ participant: { name: string; countryId: number } }>,
): string[] {
  const finnishParticipants = eventParticipants
    .filter((ep) => ep.participant.countryId === 24) // Filter Finnish participants (countryId 24 = Finland)
    .map((ep) => ep.participant.name)
    .filter((name) => name !== 'Suomi' && name !== 'Finland') // Exclude if name is exactly "Suomi" or "Finland"
    .map(extractSurname) // Extract surname (last part of name)
    .filter((surname) => surname.length > 0); // Remove empty surnames

  return finnishParticipants;
}

/**
 * Finds all valid metrics from participant results
 * Returns all metrics of type: distance, duration, missed_shots, or points
 */
function findAllMetrics(results: Array<{ type: string; value: string }>): ResultMetric[] {
  return results
    .filter((r) => VALID_METRIC_TYPES.includes(r.type as MetricType))
    .map((r) => ({ type: r.type as MetricType, value: r.value }));
}

/**
 * Determines sport type from event participants
 * Checks the first participant to determine if individual or team sport
 * Returns 'not_known' if results object is missing
 */
function determineSportsType(event: OlympicsEvent): SportsType {
  const firstParticipant = event.eventParticipants[0];

  if (firstParticipant) {
    // If results object is missing, return not_known
    if (!firstParticipant.results) {
      return 'not_known';
    }
    return firstParticipant.results.resultType === 'team' ? 'team' : 'individual';
  }

  // Default to not_known if no participants
  return 'not_known';
}

/**
 * Transforms backend Olympics event to frontend listable format
 * Assumes statusType is valid - caller should filter out invalid events first
 *
 * @param event - Backend Olympics event from API/JSON with valid statusType
 * @returns Clean, frontend-optimized listable event (discriminated union)
 */
export function transformToListableEvent(event: OlympicsEvent): OlympicsListableEvent {
  const baseEvent = {
    id: event.id,
    name: event.name,
    progression: event.statusType as ProgressionStatus,
    metadata: {
      date: event.date,
      hasFin: event.hasFin,
      tv: event.tv ?? null,
      insight: event.insight ?? null,
      correction: event.correction ?? null,
      urls: event.urls ?? [],
      medal: event.medal ?? false,
    },
  };

  const sportsType = determineSportsType(event);
  const finnishParticipants = extractFinnishParticipantSurnames(event.eventParticipants);

  // If sport type is not known, return event with unknown sport type
  if (sportsType === 'not_known') {
    return {
      ...baseEvent,
      sportsType: 'not_known',
      results: null,
      finnishParticipants,
    };
  }

  // Check if any results are available (has rank, comment, or non-empty results)
  const hasResults = event.eventParticipants.some(
    (ep) => ep.results && (ep.results.rank !== undefined || ep.results.results.length > 0),
  );

  if (sportsType === 'individual') {
    let results: IndividualSportEvent['results'] = null;

    if (hasResults) {
      // Transform all participants with results
      const allResults: IndividualResultEntry[] = event.eventParticipants
        .filter(
          (ep) => ep.results && (ep.results.rank !== undefined || ep.results.results.length > 0),
        )
        .map((ep): IndividualResultEntry => {
          // Extract status info from comment field (DNS, DNF, DSQ, Q, etc.)
          const commentMetric = ep.results!.results.find((r) => r.type === 'comment');
          const rawValue = commentMetric?.value;
          const resultComment = rawValue && isResultComment(rawValue) ? rawValue : null;

          // Check if participant qualified for next round (Q or Q/*)
          const qualified = rawValue ? isQualifiedCode(rawValue) : false;

          // Determine standing: either finished with rank or never finished with reason
          // Q and Q/* codes mean qualified (finished), not never finished
          const standing: Standing =
            resultComment && isNeverFinishedInfo(resultComment)
              ? { type: 'never_finished', reason: resultComment }
              : {
                  type: 'finished',
                  // Use 999 as failsafe rank if backend doesn't provide one (easier to spot in debugging)
                  rank: ep.results!.rank ? parseInt(ep.results!.rank, 10) : 999,
                  qualified,
                };

          return {
            participantName: normalizeCountryName(ep.participant.name),
            countryId: ep.participant.countryId,
            standing,
            medal: ep.results!.medal || null,
            metrics: findAllMetrics(ep.results!.results),
          };
        })
        .sort((a, b) => {
          // Sort by standing: finished participants first (sorted by rank), then never finished
          if (a.standing.type === 'never_finished') return 1;
          if (b.standing.type === 'never_finished') return -1;
          return a.standing.rank - b.standing.rank;
        });

      // Split into bestThree and finnsBelowThree
      const bestThree = allResults.slice(0, 3);
      const finnsBelowThree = allResults.filter((r) => {
        if (r.countryId !== 24) return false;
        // Include Finnish participants who never finished or finished with rank > 3
        if (r.standing.type === 'never_finished') return true;
        return r.standing.rank > 3;
      });

      results = {
        bestThree,
        finnsBelowThree,
      };
    }

    return {
      ...baseEvent,
      sportsType: 'individual',
      results,
      finnishParticipants,
    } as IndividualSportEvent;
  } else {
    // Team sport
    let participants: TeamSportEvent['participants'] = null;

    // For team sports, show participants even if there are no results yet
    // Check if there are any participants with results object (even if empty)
    const hasParticipantsWithResults = event.eventParticipants.some((ep) => ep.results);

    if (hasParticipantsWithResults) {
      const teams = event.eventParticipants
        .filter((ep) => ep.results)
        .map((ep): TeamResultEntry => {
          // Find the finalresult metric
          const finalResultMetric = ep.results!.results.find((r) => r.type === 'finalresult');
          return {
            teamName: normalizeCountryName(ep.participant.name),
            countryId: ep.participant.countryId,
            finalResult: finalResultMetric?.value || null,
          };
        });

      // Team sports always have exactly 2 teams
      if (teams.length >= 2) {
        participants = {
          teamOne: teams[0],
          teamTwo: teams[1],
        };
      }
    }

    return {
      ...baseEvent,
      sportsType: 'team',
      participants,
    } as TeamSportEvent;
  }
}

/**
 * Transforms multiple backend events to frontend listable format
 * Filters out events with invalid statusType values before transformation
 *
 * @param events - Array of backend Olympics events
 * @returns Array of clean, frontend-optimized listable events (excluding events with invalid statusType)
 */
export function transformToListableEvents(events: OlympicsEvent[]): OlympicsListableEvent[] {
  const invalidEvents = events.filter((event) => !isProgressionStatus(event.statusType));

  if (invalidEvents.length > 0) {
    console.warn(
      `Dropping ${invalidEvents.length} event(s) with invalid statusType:`,
      invalidEvents.map((e) => ({ id: e.id, name: e.name, statusType: e.statusType })),
    );
  }

  return events
    .filter((event) => isProgressionStatus(event.statusType))
    .map(transformToListableEvent);
}
